import React, { useState, useCallback, useMemo } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Switch } from '@/components/ui/switch';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  Popover, PopoverContent, PopoverTrigger,
} from '@/components/ui/popover';
import {
  Plus, Trash2, GripVertical, Link, Calculator, GitBranch, Scissors, Receipt, FileSpreadsheet,
  ChevronDown, Search, ArrowRight, Wand2, TrendingUp, Percent, DollarSign,
} from 'lucide-react';

import {
  CalculatedConfig,
  CalcType,
  ConcatPart,
  MathInput,
  MathOperator,
  ConditionalOperator,
  TransformType,
  GstOutputMode,
  GstInputMode,
  ExcelFormulaResultType,
  MarginRule,
  ExportTemplateColumn,
  SourceFieldOption,
  OtherTemplateRulesGroup,
  CALC_TYPE_INFO,
  MATH_OPERATOR_INFO,
  CONDITIONAL_OPERATOR_INFO,
  SEPARATOR_PRESETS,
  SPLIT_DELIMITER_PRESETS,
  SPLIT_SELECT_JOIN_PRESETS,

  TRANSFORM_TYPE_INFO,
  DATE_FORMAT_OPTIONS,
  SOURCE_FIELD_OPTIONS,

  SOURCE_FIELD_GROUPS,
  GST_OUTPUT_MODE_INFO,
  GST_INPUT_MODE_INFO,
  NUMERIC_SOURCE_FIELDS,
  CALC_COL_PREFIX,
  makeCalcColField,
  isCalcColField,
  getCalcColId,
  makeMarginField,
  isMarginField,
  getMarginRuleId,
} from './types';
import { getMarginFactor } from './transforms';



// ─── Calc type label helpers for template column descriptions ───────
const CALC_TYPE_LABELS: Record<string, string> = {
  concat: 'Concat',
  math: 'Math',
  conditional_text: 'Conditional',
  split: 'Split',
  gst: 'GST',
  excel_formula: 'Excel Formula',
};

interface CalculatedColumnBuilderProps {
  config: CalculatedConfig | null;
  onChange: (config: CalculatedConfig) => void;
  marginRules?: MarginRule[];
  /** Margin rules from other templates, grouped by template */
  otherTemplateRulesGroups?: OtherTemplateRulesGroup[];
  /** All template columns — used to allow referencing other calculated columns */
  templateColumns?: ExportTemplateColumn[];
  /** The ID of the column currently being edited (to prevent self-reference) */
  currentColumnId?: string;
}


// ─── Field Picker (inline) ──────────────────────────────────────────

const InlineFieldPicker: React.FC<{
  value: string | undefined;
  onChange: (field: string) => void;
  label?: string;
  /** Extra fields from other template columns (calculated, source_field, etc.) */
  extraFields?: SourceFieldOption[];
  /** Margin rules to show as selectable factor fields */
  marginRules?: MarginRule[];
}> = ({ value, onChange, label, extraFields = [], marginRules = [] }) => {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');

  // Build margin factor field options from active margin rules
  const marginFactorFields: SourceFieldOption[] = useMemo(() => {
    return marginRules
      .filter(r => r.is_active)
      .map(rule => {
        const factor = getMarginFactor(rule);
        const desc = rule.margin_type === 'percentage'
          ? `×${factor.toFixed(4)}`
          : `$${rule.margin_value}`;
        return {
          field: makeMarginField(rule.id),
          label: rule.rule_name,
          group: 'Margin Factors',
          description: desc,
        };
      });
  }, [marginRules]);

  // Combine standard fields with extra template column fields and margin factor fields
  const allFields = useMemo(() => [...SOURCE_FIELD_OPTIONS, ...extraFields, ...marginFactorFields], [extraFields, marginFactorFields]);
  const allGroups = useMemo(() => {
    const groups = [...SOURCE_FIELD_GROUPS] as string[];
    // Add "Template Columns" group if there are extra fields
    if (extraFields.length > 0 && !groups.includes('Template Columns')) {
      groups.unshift('Template Columns');
    }
    // Add "Margin Factors" group if there are margin rules
    if (marginFactorFields.length > 0 && !groups.includes('Margin Factors')) {
      // Insert after Derived Fields
      const derivedIdx = groups.indexOf('Derived Fields');
      if (derivedIdx >= 0) {
        groups.splice(derivedIdx + 1, 0, 'Margin Factors');
      } else {
        groups.push('Margin Factors');
      }
    }
    return groups;
  }, [extraFields, marginFactorFields]);

  const filtered = allFields.filter(f =>
    !search || f.label.toLowerCase().includes(search.toLowerCase()) || f.field.toLowerCase().includes(search.toLowerCase())
  );

  // Resolve label for the current value (could be a calc col or margin rule reference)
  const fieldLabel = useMemo(() => {
    if (!value) return null;
    const found = allFields.find(f => f.field === value);
    if (found) return found.label;
    // Fallback: if it's a calc col reference, show the raw ID
    if (isCalcColField(value)) return `Column: ${getCalcColId(value)}`;
    // Fallback: if it's a margin rule reference, show the raw ID
    if (isMarginField(value)) return `Margin: ${getMarginRuleId(value)}`;
    return value;
  }, [value, allFields]);

  return (
    <Popover open={open} onOpenChange={(o) => { setOpen(o); if (!o) setSearch(''); }}>
      <PopoverTrigger asChild>
        <Button variant="outline" className="w-full justify-between dark-input text-xs h-7 font-normal">
          {fieldLabel ? (
            <span className="truncate text-foreground flex items-center gap-1">
              {value && isCalcColField(value) && (
                <Calculator className="w-2.5 h-2.5 text-amber-400 flex-shrink-0" />
              )}
              {value && isMarginField(value) && (
                <TrendingUp className="w-2.5 h-2.5 text-emerald-400 flex-shrink-0" />
              )}
              {fieldLabel}
            </span>
          ) : (
            <span className="text-muted-foreground">{label || 'Select field...'}</span>
          )}
          <ChevronDown className="w-3 h-3 text-muted-foreground flex-shrink-0" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-64 p-0 max-h-[300px] overflow-hidden" align="start">
        <div className="p-2 border-b border-border">
          <div className="relative">
            <Search className="w-3 h-3 absolute left-2 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search fields..." className="dark-input text-xs h-7 pl-7" autoFocus />
          </div>
        </div>
        <div className="overflow-y-auto max-h-[240px] p-1">
          {allGroups.map(group => {
            const groupFields = filtered.filter(f => f.group === group);
            if (groupFields.length === 0) return null;
            return (
              <div key={group} className="mb-1">
                <p className={`text-[9px] font-semibold uppercase tracking-wider px-2 py-1 ${
                  group === 'Template Columns' ? 'text-amber-400' :
                  group === 'Margin Factors' ? 'text-emerald-400' :
                  'text-muted-foreground'
                }`}>{group}</p>
                {groupFields.map(f => (
                  <button key={f.field}
                    className={`w-full text-left px-2 py-1 rounded text-xs hover:bg-secondary/60 transition-colors flex items-center gap-1.5 ${value === f.field ? 'bg-primary/10 text-primary' : 'text-foreground'}`}
                    onClick={() => { onChange(f.field); setOpen(false); }}>
                    {isCalcColField(f.field) && (
                      <Calculator className="w-2.5 h-2.5 text-amber-400 flex-shrink-0" />
                    )}
                    {isMarginField(f.field) && (
                      <TrendingUp className="w-2.5 h-2.5 text-emerald-400 flex-shrink-0" />
                    )}
                    <span className="truncate">{f.label}</span>
                    {f.description && (
                      <span className="text-[9px] text-muted-foreground/50 ml-auto flex-shrink-0">{f.description}</span>
                    )}
                  </button>
                ))}
              </div>
            );
          })}
        </div>
      </PopoverContent>
    </Popover>
  );
};



// ─── Concat Builder ─────────────────────────────────────────────────

const ConcatBuilder: React.FC<{
  config: CalculatedConfig;
  onChange: (config: CalculatedConfig) => void;
}> = ({ config, onChange }) => {
  const parts = config.parts || [];

  const updatePart = (idx: number, updates: Partial<ConcatPart>) => {
    const newParts = [...parts];
    newParts[idx] = { ...newParts[idx], ...updates };
    onChange({ ...config, parts: newParts });
  };

  const addPart = (partType: ConcatPart['partType']) => {
    const newPart: ConcatPart = partType === 'field'
      ? { partType: 'field', field: '' }
      : partType === 'separator'
        ? { partType: 'separator', value: ' ' }
        : { partType: 'text', value: '' };
    onChange({ ...config, parts: [...parts, newPart] });
  };

  const removePart = (idx: number) => {
    onChange({ ...config, parts: parts.filter((_, i) => i !== idx) });
  };

  const movePart = (fromIdx: number, toIdx: number) => {
    if (toIdx < 0 || toIdx >= parts.length) return;
    const newParts = [...parts];
    const [moved] = newParts.splice(fromIdx, 1);
    newParts.splice(toIdx, 0, moved);
    onChange({ ...config, parts: newParts });
  };

  return (
    <div className="space-y-2">
      <div className="flex items-center gap-1.5 mb-1">
        <Link className="w-3 h-3 text-violet-400" />
        <span className="text-[10px] font-semibold text-violet-400 uppercase">Concatenation Parts</span>
        <Badge className="bg-violet-900/30 text-violet-400 text-[9px] px-1 py-0">{parts.length}</Badge>
      </div>

      {parts.length === 0 && (
        <p className="text-[10px] text-muted-foreground/60 py-2 text-center">No parts added yet. Add fields, text, or separators below.</p>
      )}

      <div className="space-y-1.5">
        {parts.map((part, idx) => (
          <div key={idx} className="flex items-start gap-1 bg-secondary/20 rounded-md p-1.5 border border-border/40">
            {/* Reorder buttons */}
            <div className="flex flex-col gap-0.5 pt-0.5">
              <button
                className="text-muted-foreground/40 hover:text-muted-foreground disabled:opacity-20"
                disabled={idx === 0}
                onClick={() => movePart(idx, idx - 1)}
                title="Move up"
              >
                <GripVertical className="w-3 h-3" />
              </button>
            </div>

            {/* Part index */}
            <span className="text-[9px] text-muted-foreground/40 w-4 text-center pt-1.5 flex-shrink-0">{idx + 1}</span>

            {/* Part content */}
            <div className="flex-1 min-w-0 space-y-1">
              {/* Part type badge */}
              <div className="flex items-center gap-1.5">
                <Badge className={`text-[9px] px-1 py-0 ${
                  part.partType === 'field' ? 'bg-blue-900/40 text-blue-400 border-blue-700/50' :
                  part.partType === 'separator' ? 'bg-orange-900/40 text-orange-400 border-orange-700/50' :
                  'bg-green-900/40 text-green-400 border-green-700/50'
                }`}>
                  {part.partType === 'field' ? 'Field' : part.partType === 'separator' ? 'Sep' : 'Text'}
                </Badge>

                {/* Quick preview of value */}
                {part.partType === 'field' && part.field && (
                  <span className="text-[9px] text-muted-foreground truncate">
                    {SOURCE_FIELD_OPTIONS.find(f => f.field === part.field)?.label || part.field}
                  </span>
                )}
                {part.partType === 'text' && part.value && (
                  <span className="text-[9px] text-green-400/70 truncate">"{part.value}"</span>
                )}
                {part.partType === 'separator' && (
                  <span className="text-[9px] text-orange-400/70">
                    {SEPARATOR_PRESETS.find(s => s.value === part.value)?.label || `"${part.value}"`}
                  </span>
                )}
              </div>

              {/* Field picker */}
              {part.partType === 'field' && (
                <div className="space-y-1">
                  <InlineFieldPicker
                    value={part.field}
                    onChange={(field) => updatePart(idx, { field })}
                  />
                  {/* Per-part transform */}
                  {part.field && (
                    <div className="space-y-1">
                      <div className="flex items-center gap-1">
                        <Wand2 className="w-2.5 h-2.5 text-cyan-400/60 flex-shrink-0" />
                        <Select
                          value={part.transform || 'none'}
                          onValueChange={(v) => {
                            const t = v as TransformType;
                            let newTransformConfig: Record<string, any> | undefined;
                            if (t === 'date_format') {
                              newTransformConfig = { format: 'DD/MM/YYYY' };
                            } else if (t === 'split_select') {
                              newTransformConfig = {
                                splitSelectDelimiter: '/',
                                splitSelectParts: [],
                                splitSelectAll: true,
                                splitSelectJoinWith: '/',
                                splitSelectTrimParts: true,
                              };
                            } else {
                              newTransformConfig = undefined;
                            }
                            updatePart(idx, {
                              transform: t === 'none' ? undefined : t,
                              transformConfig: newTransformConfig,
                            });
                          }}
                        >
                          <SelectTrigger className="dark-input text-[10px] h-6 flex-1"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            {(['none', 'clean_address', 'date_format', 'trim', 'split_select'] as TransformType[]).map(tt => (
                              <SelectItem key={tt} value={tt}>
                                <span className="text-[10px]">{TRANSFORM_TYPE_INFO[tt].label}</span>
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        {part.transform === 'date_format' && (
                          <Select
                            value={part.transformConfig?.format || 'DD/MM/YYYY'}
                            onValueChange={(v) => updatePart(idx, { transformConfig: { format: v } })}
                          >
                            <SelectTrigger className="dark-input text-[10px] h-6 w-28"><SelectValue /></SelectTrigger>
                            <SelectContent>
                              {DATE_FORMAT_OPTIONS.map(opt => (
                                <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        )}
                      </div>

                      {/* ─── Split & Select Config (inline for concat part) ─── */}
                      {part.transform === 'split_select' && (() => {
                        const cfg = part.transformConfig || {};
                        const delimiter: string = cfg.splitSelectDelimiter || '/';
                        const selectAll: boolean = cfg.splitSelectAll ?? true;
                        const selectedParts: number[] = cfg.splitSelectParts || [];
                        const joinWith: string = cfg.splitSelectJoinWith ?? '/';
                        const trimParts: boolean = cfg.splitSelectTrimParts !== false;

                        const updateSplitCfg = (updates: Record<string, any>) => {
                          updatePart(idx, { transformConfig: { ...cfg, ...updates } });
                        };

                        const togglePartSel = (partIdx: number) => {
                          const next = selectedParts.includes(partIdx)
                            ? selectedParts.filter(p => p !== partIdx)
                            : [...selectedParts, partIdx].sort((a, b) => a - b);
                          updateSplitCfg({ splitSelectParts: next, splitSelectAll: false });
                        };

                        // Example preview
                        const exValue = 'Value1 / Value2 / Value3';
                        const exParts = exValue.split(delimiter);
                        let exResult = '';
                        if (selectAll) {
                          exResult = exParts.map(p => trimParts ? p.trim() : p).filter(p => p !== '').join(joinWith);
                        } else if (selectedParts.length > 0) {
                          exResult = selectedParts
                            .sort((a, b) => a - b)
                            .map(i => i >= 0 && i < exParts.length ? (trimParts ? exParts[i].trim() : exParts[i]) : '')
                            .filter(p => p !== '')
                            .join(joinWith);
                        } else {
                          exResult = exValue;
                        }

                        const MAX_PARTS = 10;

                        return (
                          <div className="mt-1 border-t border-sky-700/20 pt-1.5 space-y-1.5">
                            <div className="flex items-center gap-1">
                              <Scissors className="w-2.5 h-2.5 text-sky-400" />
                              <span className="text-[9px] font-semibold text-sky-400 uppercase">Split & Select Settings</span>
                            </div>

                            {/* Delimiter */}
                            <div>
                              <Label className="text-[9px] text-muted-foreground uppercase">Split Delimiter</Label>
                              <Select value={delimiter} onValueChange={(v) => updateSplitCfg({ splitSelectDelimiter: v })}>
                                <SelectTrigger className="dark-input text-[10px] h-6"><SelectValue /></SelectTrigger>
                                <SelectContent>
                                  {SPLIT_DELIMITER_PRESETS.map(opt => (
                                    <SelectItem key={opt.value} value={opt.value}>
                                      <span className="font-mono text-[10px]">{opt.label}</span>
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                              <p className="text-[8px] text-muted-foreground/50 mt-0.5">The character(s) to split the value by</p>
                            </div>

                            {/* Parts to Include */}
                            <div>
                              <Label className="text-[9px] text-muted-foreground uppercase">Parts to Include</Label>
                              <div className="mt-0.5 space-y-1">
                                <label className="flex items-center gap-1.5 cursor-pointer px-1 py-0.5 rounded hover:bg-sky-900/10 transition-colors">
                                  <Checkbox
                                    checked={selectAll}
                                    onCheckedChange={(v) => updateSplitCfg({ splitSelectAll: !!v, splitSelectParts: [] })}
                                    className="h-3 w-3"
                                  />
                                  <span className="text-[9px] font-medium text-sky-400">All Parts</span>
                                  <span className="text-[8px] text-muted-foreground/50 ml-auto">Include every value after splitting</span>
                                </label>

                                {!selectAll && (
                                  <div className="border border-sky-700/20 rounded p-1 bg-sky-900/5">
                                    <p className="text-[8px] text-muted-foreground/60 mb-0.5">Select which parts to keep (1-indexed positions):</p>
                                    <div className="flex flex-wrap gap-0.5">
                                      {Array.from({ length: MAX_PARTS }, (_, i) => (
                                        <button
                                          key={i}
                                          onClick={() => togglePartSel(i)}
                                          className={`min-w-[22px] px-1 py-0.5 rounded text-[9px] font-medium border transition-colors ${
                                            selectedParts.includes(i)
                                              ? 'bg-sky-900/40 text-sky-300 border-sky-600/50'
                                              : 'bg-secondary/30 text-muted-foreground border-border/50 hover:border-sky-700/30 hover:text-sky-400'
                                          }`}
                                        >
                                          {i + 1}
                                        </button>
                                      ))}
                                    </div>
                                    {selectedParts.length > 0 && (
                                      <p className="text-[8px] text-sky-400/70 mt-1">
                                        Keeping part{selectedParts.length > 1 ? 's' : ''}: {selectedParts.map(p => p + 1).join(', ')}
                                      </p>
                                    )}
                                  </div>
                                )}
                              </div>
                            </div>

                            {/* Join Separator */}
                            <div>
                              <Label className="text-[9px] text-muted-foreground uppercase">Join Separator</Label>
                              <Select value={joinWith} onValueChange={(v) => updateSplitCfg({ splitSelectJoinWith: v })}>
                                <SelectTrigger className="dark-input text-[10px] h-6"><SelectValue /></SelectTrigger>
                                <SelectContent>
                                  {SPLIT_SELECT_JOIN_PRESETS.map(opt => (
                                    <SelectItem key={opt.value} value={opt.value}>
                                      <span className="font-mono text-[10px]">{opt.label}</span>
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                              <p className="text-[8px] text-muted-foreground/50 mt-0.5">How selected parts are joined back together</p>
                            </div>

                            {/* Trim Toggle */}
                            <label className="flex items-center gap-1.5 cursor-pointer">
                              <Switch
                                checked={trimParts}
                                onCheckedChange={(v) => updateSplitCfg({ splitSelectTrimParts: v })}
                                className="scale-[0.6]"
                              />
                              <span className="text-[9px] text-muted-foreground">Trim whitespace from each part</span>
                            </label>

                            {/* Preview */}
                            <div className="bg-sky-900/15 border border-sky-700/25 rounded px-2 py-1.5">
                              <div className="flex items-center justify-between">
                                <span className="text-[8px] text-sky-400/70 uppercase">Preview</span>
                                <span className="text-[8px] text-muted-foreground/50">
                                  Parts: {selectAll ? 'All' : selectedParts.length > 0 ? selectedParts.map(p => p + 1).join(', ') : 'none'}
                                </span>
                              </div>
                              <div className="text-[9px] text-muted-foreground/60 mt-0.5 font-mono">Input: &quot;{exValue}&quot;</div>
                              <div className="text-[10px] font-mono text-sky-300 mt-0.5 font-semibold">{exResult || '(empty)'}</div>
                            </div>
                          </div>
                        );
                      })()}
                    </div>
                  )}
                </div>
              )}


              {/* Text input */}
              {part.partType === 'text' && (
                <Input
                  value={part.value || ''}
                  onChange={(e) => updatePart(idx, { value: e.target.value })}
                  placeholder="Enter text..."
                  className="dark-input text-xs h-6"
                />
              )}

              {/* Separator picker */}
              {part.partType === 'separator' && (
                <Select
                  value={part.value || ' '}
                  onValueChange={(v) => updatePart(idx, { value: v })}
                >
                  <SelectTrigger className="dark-input text-xs h-6"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {SEPARATOR_PRESETS.map(sep => (
                      <SelectItem key={sep.value} value={sep.value}>{sep.label}</SelectItem>
                    ))}
                    <SelectItem value="__custom">Custom...</SelectItem>
                  </SelectContent>
                </Select>
              )}
              {part.partType === 'separator' && !SEPARATOR_PRESETS.some(s => s.value === part.value) && (
                <Input
                  value={part.value || ''}
                  onChange={(e) => updatePart(idx, { value: e.target.value })}
                  placeholder="Custom separator..."
                  className="dark-input text-xs h-6 mt-1"
                />
              )}
            </div>

            {/* Delete */}
            <button className="text-muted-foreground/40 hover:text-red-400 mt-0.5 flex-shrink-0" onClick={() => removePart(idx)}>
              <Trash2 className="w-3 h-3" />
            </button>
          </div>
        ))}
      </div>

      {/* Add part buttons */}
      <div className="flex gap-1 pt-1">
        <Button size="sm" variant="outline" className="h-6 text-[10px] px-2 border-blue-700/30 text-blue-400 hover:bg-blue-900/20" onClick={() => addPart('field')}>
          <Plus className="w-2.5 h-2.5 mr-0.5" /> Field
        </Button>
        <Button size="sm" variant="outline" className="h-6 text-[10px] px-2 border-green-700/30 text-green-400 hover:bg-green-900/20" onClick={() => addPart('text')}>
          <Plus className="w-2.5 h-2.5 mr-0.5" /> Text
        </Button>
        <Button size="sm" variant="outline" className="h-6 text-[10px] px-2 border-orange-700/30 text-orange-400 hover:bg-orange-900/20" onClick={() => addPart('separator')}>
          <Plus className="w-2.5 h-2.5 mr-0.5" /> Separator
        </Button>
      </div>

      {/* Preview formula */}
      {parts.length > 0 && (
        <div className="bg-secondary/30 rounded px-2 py-1.5 border border-border/30 mt-1">
          <p className="text-[9px] text-muted-foreground/60 uppercase mb-0.5">Preview Formula</p>
          <p className="text-[10px] text-foreground/80 font-mono break-all">
            {parts.map((p, i) => {
              if (p.partType === 'field') {
                const lbl = SOURCE_FIELD_OPTIONS.find(f => f.field === p.field)?.label || p.field || '?';
                const tfx = p.transform ? ` [${TRANSFORM_TYPE_INFO[p.transform as TransformType]?.label || p.transform}]` : '';
                return <span key={i} className="text-blue-400">{`{${lbl}${tfx}}`}</span>;
              }
              if (p.partType === 'separator') {
                return <span key={i} className="text-orange-400">{p.value}</span>;
              }
              return <span key={i} className="text-green-400">"{p.value}"</span>;
            })}
          </p>
        </div>
      )}
    </div>
  );
};

// ─── Math Builder ───────────────────────────────────────────────────
const MathBuilder: React.FC<{
  config: CalculatedConfig;
  onChange: (config: CalculatedConfig) => void;
  marginRules?: MarginRule[];
  extraFields?: SourceFieldOption[];
}> = ({ config, onChange, marginRules = [], extraFields = [] }) => {

  const inputs = config.inputs || [];

  // Parse into value items and operators
  const valueItems: MathInput[] = inputs.filter(i => i.inputType);
  const operators: MathOperator[] = inputs.filter(i => i.operator).map(i => i.operator!);

  const rebuildInputs = (newValues: MathInput[], newOps: MathOperator[]) => {
    const result: MathInput[] = [];
    for (let i = 0; i < newValues.length; i++) {
      result.push(newValues[i]);
      if (i < newOps.length) {
        result.push({ operator: newOps[i] });
      }
    }
    onChange({ ...config, inputs: result });
  };

  const updateValue = (idx: number, updates: Partial<MathInput>) => {
    const newValues = [...valueItems];
    newValues[idx] = { ...newValues[idx], ...updates };
    rebuildInputs(newValues, operators);
  };

  const updateOperator = (idx: number, op: MathOperator) => {
    const newOps = [...operators];
    newOps[idx] = op;
    rebuildInputs(valueItems, newOps);
  };

  const addInput = () => {
    if (valueItems.length >= 4) return;
    const newValues = [...valueItems, { inputType: 'field' as const, field: '' }];
    const newOps = [...operators, 'add' as MathOperator];
    rebuildInputs(newValues, newOps);
  };

  const removeInput = (idx: number) => {
    if (valueItems.length <= 1) return;
    const newValues = valueItems.filter((_, i) => i !== idx);
    const newOps = [...operators];
    if (idx > 0) newOps.splice(idx - 1, 1);
    else if (newOps.length > 0) newOps.splice(0, 1);
    rebuildInputs(newValues, newOps);
  };

  // Helper to get margin rule label
  const getMarginRuleLabel = (ruleId: string | undefined) => {
    if (!ruleId) return null;
    const rule = marginRules.find(r => r.id === ruleId);
    if (!rule) return null;
    const factor = getMarginFactor(rule);
    return `${rule.rule_name} (×${factor.toFixed(4)})`;
  };

  return (
    <div className="space-y-2">
      <div className="flex items-center gap-1.5 mb-1">
        <Calculator className="w-3 h-3 text-amber-400" />
        <span className="text-[10px] font-semibold text-amber-400 uppercase">Math Expression</span>
        <Badge className="bg-amber-900/30 text-amber-400 text-[9px] px-1 py-0">{valueItems.length} inputs</Badge>
      </div>

      <div className="space-y-1.5">
        {valueItems.map((item, idx) => (
          <React.Fragment key={idx}>
            {/* Operator between items */}
            {idx > 0 && idx - 1 < operators.length && (
              <div className="flex items-center justify-center gap-1 py-0.5">
                <div className="h-px flex-1 bg-border/30" />
                <Select
                  value={operators[idx - 1]}
                  onValueChange={(v) => updateOperator(idx - 1, v as MathOperator)}
                >
                  <SelectTrigger className="dark-input text-xs h-6 w-24 text-center">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {(Object.keys(MATH_OPERATOR_INFO) as MathOperator[]).map(op => (
                      <SelectItem key={op} value={op}>
                        <span className="flex items-center gap-1.5">
                          <span className="font-mono text-amber-400">{MATH_OPERATOR_INFO[op].symbol}</span>
                          <span>{MATH_OPERATOR_INFO[op].label}</span>
                        </span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <div className="h-px flex-1 bg-border/30" />
              </div>
            )}

            {/* Value input */}
            <div className={`flex items-center gap-1.5 rounded-md p-1.5 border ${item.inputType === 'margin' ? 'bg-emerald-900/10 border-emerald-700/30' : 'bg-secondary/20 border-border/40'}`}>
              <span className="text-[9px] text-muted-foreground/40 w-4 text-center flex-shrink-0">{idx + 1}</span>

              {/* Input type toggle - now with Margin option */}
              <Select
                value={item.inputType || 'field'}
                onValueChange={(v) => {
                  if (v === 'field') updateValue(idx, { inputType: 'field', field: '', value: undefined, marginRuleId: undefined });
                  else if (v === 'static') updateValue(idx, { inputType: 'static', value: 0, field: undefined, marginRuleId: undefined });
                  else if (v === 'margin') updateValue(idx, { inputType: 'margin', marginRuleId: marginRules[0]?.id || '', field: undefined, value: undefined });
                }}
              >
                <SelectTrigger className={`dark-input text-[10px] h-6 flex-shrink-0 ${item.inputType === 'margin' ? 'w-20 border-emerald-700/40' : 'w-16'}`}>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="field">
                    <span className="flex items-center gap-1"><span>Field</span></span>
                  </SelectItem>
                  <SelectItem value="static">
                    <span className="flex items-center gap-1"><span>Number</span></span>
                  </SelectItem>
                  {marginRules.length > 0 && (
                    <SelectItem value="margin">
                      <span className="flex items-center gap-1">
                        <TrendingUp className="w-3 h-3 text-emerald-400" />
                        <span>Margin</span>
                      </span>
                    </SelectItem>
                  )}
                </SelectContent>
              </Select>

              {/* Field picker */}
              {item.inputType === 'field' && (
                <div className="flex-1">
                  <InlineFieldPicker value={item.field} onChange={(field) => updateValue(idx, { field })} extraFields={extraFields} marginRules={marginRules} />
                </div>


              )}

              {/* Static number */}
              {item.inputType === 'static' && (
                <Input
                  type="number"
                  step="any"
                  value={item.value ?? ''}
                  onChange={(e) => updateValue(idx, { value: e.target.value === '' ? undefined : Number(e.target.value) })}
                  placeholder="0"
                  className="dark-input text-xs h-6 flex-1"
                />
              )}

              {/* Margin rule picker */}
              {item.inputType === 'margin' && (
                <div className="flex-1 space-y-1">
                  <Select
                    value={item.marginRuleId || ''}
                    onValueChange={(v) => updateValue(idx, { marginRuleId: v })}
                  >
                    <SelectTrigger className="dark-input text-[10px] h-6 border-emerald-700/30">
                      <SelectValue placeholder="Select margin rule..." />
                    </SelectTrigger>
                    <SelectContent>
                      {marginRules.filter(r => r.is_active).map(rule => {
                        const factor = getMarginFactor(rule);
                        return (
                          <SelectItem key={rule.id} value={rule.id}>
                            <span className="flex items-center gap-1.5">
                              {rule.margin_type === 'percentage' ? (
                                <Percent className="w-3 h-3 text-blue-400 flex-shrink-0" />
                              ) : (
                                <DollarSign className="w-3 h-3 text-green-400 flex-shrink-0" />
                              )}
                              <span className="truncate">{rule.rule_name}</span>
                              <span className="text-[9px] text-emerald-400 font-mono flex-shrink-0">
                                {rule.margin_type === 'percentage' ? `×${factor.toFixed(2)}` : `$${rule.margin_value}`}
                              </span>
                            </span>
                          </SelectItem>
                        );
                      })}
                      {marginRules.filter(r => r.is_active).length === 0 && (
                        <div className="px-2 py-1.5 text-[10px] text-muted-foreground/60">No active margin rules</div>
                      )}
                    </SelectContent>
                  </Select>
                  {/* Show factor preview */}
                  {item.marginRuleId && (() => {
                    const rule = marginRules.find(r => r.id === item.marginRuleId);
                    if (!rule) return null;
                    const factor = getMarginFactor(rule);
                    return (
                      <div className="flex items-center gap-1 text-[9px]">
                        <TrendingUp className="w-2.5 h-2.5 text-emerald-400/70" />
                        <span className="text-emerald-400/70">
                          {rule.margin_type === 'percentage'
                            ? `${rule.margin_value >= 0 ? '+' : ''}${rule.margin_value}% → factor ${factor.toFixed(4)}`
                            : `$${rule.margin_value >= 0 ? '+' : ''}${rule.margin_value}`
                          }
                        </span>
                        {rule.target_scope !== 'all_rows' && (
                          <Badge className="bg-orange-900/30 text-orange-400 border-orange-700/30 text-[8px] px-1 py-0">
                            {rule.target_config?.value || rule.target_scope}
                          </Badge>
                        )}
                      </div>
                    );
                  })()}
                </div>
              )}

              {/* Remove */}
              {valueItems.length > 1 && (
                <button className="text-muted-foreground/40 hover:text-red-400 flex-shrink-0" onClick={() => removeInput(idx)}>
                  <Trash2 className="w-3 h-3" />
                </button>
              )}
            </div>
          </React.Fragment>
        ))}
      </div>

      {/* Add input */}
      {valueItems.length < 4 && (
        <Button size="sm" variant="outline" className="h-6 text-[10px] px-2 border-amber-700/30 text-amber-400 hover:bg-amber-900/20" onClick={addInput}>
          <Plus className="w-2.5 h-2.5 mr-0.5" /> Add Input
        </Button>
      )}

      {/* Preview formula */}
      {valueItems.length > 0 && (
        <div className="bg-secondary/30 rounded px-2 py-1.5 border border-border/30">
          <p className="text-[9px] text-muted-foreground/60 uppercase mb-0.5">Preview Formula</p>
          <p className="text-[10px] text-foreground/80 font-mono">
            {valueItems.map((item, idx) => (
              <React.Fragment key={idx}>
                {idx > 0 && idx - 1 < operators.length && (
                  <span className="text-amber-400 mx-1">{MATH_OPERATOR_INFO[operators[idx - 1]]?.symbol || '?'}</span>
                )}
                {item.inputType === 'field' ? (
                  (() => {
                    const fieldVal = item.field;
                    const stdLabel = SOURCE_FIELD_OPTIONS.find(f => f.field === fieldVal)?.label;
                    const extraLabel = extraFields.find(f => f.field === fieldVal)?.label;
                    const label = stdLabel || extraLabel || fieldVal || '?';
                    const isCalcRef = fieldVal ? isCalcColField(fieldVal) : false;
                    return <span className={isCalcRef ? 'text-amber-400' : 'text-blue-400'}>{label}</span>;
                  })()

                ) : item.inputType === 'margin' ? (
                  <span className="text-emerald-400">
                    {(() => {
                      const rule = marginRules.find(r => r.id === item.marginRuleId);
                      if (!rule) return 'Margin(?)';
                      const factor = getMarginFactor(rule);
                      return `Margin[${rule.rule_name}](${factor.toFixed(2)})`;
                    })()}
                  </span>
                ) : (
                  <span className="text-green-400">{item.value ?? 0}</span>
                )}
              </React.Fragment>
            ))}
          </p>
        </div>
      )}
    </div>
  );
};


// ─── Conditional Text Builder ───────────────────────────────────────

const ConditionalTextBuilder: React.FC<{
  config: CalculatedConfig;
  onChange: (config: CalculatedConfig) => void;
}> = ({ config, onChange }) => {
  const condition = config.condition || { field: '', operator: 'is_not_blank' as ConditionalOperator };
  const thenValue = config.thenValue || '';
  const elseValue = config.elseValue || '';

  const opInfo = CONDITIONAL_OPERATOR_INFO[condition.operator as ConditionalOperator];

  return (
    <div className="space-y-2">
      <div className="flex items-center gap-1.5 mb-1">
        <GitBranch className="w-3 h-3 text-rose-400" />
        <span className="text-[10px] font-semibold text-rose-400 uppercase">Conditional Text</span>
      </div>

      {/* IF condition */}
      <div className="bg-secondary/20 rounded-md p-2 border border-border/40 space-y-1.5">
        <div className="flex items-center gap-1.5">
          <Badge className="bg-rose-900/40 text-rose-400 text-[9px] px-1.5 py-0">IF</Badge>
        </div>

        {/* Field */}
        <div>
          <Label className="text-[9px] text-muted-foreground uppercase">Field</Label>
          <InlineFieldPicker
            value={condition.field}
            onChange={(field) => onChange({ ...config, condition: { ...condition, field } })}
          />
        </div>

        {/* Operator */}
        <div>
          <Label className="text-[9px] text-muted-foreground uppercase">Condition</Label>
          <Select
            value={condition.operator}
            onValueChange={(v) => onChange({ ...config, condition: { ...condition, operator: v as ConditionalOperator } })}
          >
            <SelectTrigger className="dark-input text-xs h-7"><SelectValue /></SelectTrigger>
            <SelectContent>
              {(Object.keys(CONDITIONAL_OPERATOR_INFO) as ConditionalOperator[]).map(op => (
                <SelectItem key={op} value={op}>{CONDITIONAL_OPERATOR_INFO[op].label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Value (if needed) */}
        {opInfo?.needsValue && (
          <div>
            <Label className="text-[9px] text-muted-foreground uppercase">Value</Label>
            <Input
              value={condition.value ?? ''}
              onChange={(e) => onChange({ ...config, condition: { ...condition, value: e.target.value } })}
              placeholder="Compare value..."
              className="dark-input text-xs h-7"
            />
          </div>
        )}
      </div>

      {/* THEN */}
      <div className="bg-green-900/10 rounded-md p-2 border border-green-700/20 space-y-1">
        <div className="flex items-center gap-1.5">
          <Badge className="bg-green-900/40 text-green-400 text-[9px] px-1.5 py-0">THEN</Badge>
          <span className="text-[9px] text-muted-foreground">Output this text</span>
        </div>
        <Input
          value={thenValue}
          onChange={(e) => onChange({ ...config, thenValue: e.target.value })}
          placeholder="Text when condition is true..."
          className="dark-input text-xs h-7"
        />
      </div>

      {/* ELSE */}
      <div className="bg-gray-800/20 rounded-md p-2 border border-gray-700/20 space-y-1">
        <div className="flex items-center gap-1.5">
          <Badge className="bg-gray-800/40 text-gray-400 text-[9px] px-1.5 py-0">ELSE</Badge>
          <span className="text-[9px] text-muted-foreground">Output this text (or leave blank)</span>
        </div>
        <Input
          value={elseValue}
          onChange={(e) => onChange({ ...config, elseValue: e.target.value })}
          placeholder="Text when condition is false (optional)..."
          className="dark-input text-xs h-7"
        />
      </div>

      {/* Preview formula */}
      <div className="bg-secondary/30 rounded px-2 py-1.5 border border-border/30">
        <p className="text-[9px] text-muted-foreground/60 uppercase mb-0.5">Preview Logic</p>
        <p className="text-[10px] text-foreground/80 font-mono">
          <span className="text-rose-400">IF </span>
          <span className="text-blue-400">{SOURCE_FIELD_OPTIONS.find(f => f.field === condition.field)?.label || condition.field || '?'}</span>
          <span className="text-muted-foreground"> {opInfo?.label || condition.operator}</span>
          {opInfo?.needsValue && <span className="text-green-400"> {condition.value ?? '?'}</span>}
          <span className="text-rose-400"> → </span>
          <span className="text-green-400">"{thenValue || '(empty)'}"</span>
          <span className="text-muted-foreground"> else </span>
          <span className="text-gray-400">"{elseValue || '(empty)'}"</span>
        </p>
      </div>
    </div>
  );
};
// ─── Split Builder ──────────────────────────────────────────────────

const SplitBuilder: React.FC<{
  config: CalculatedConfig;
  onChange: (config: CalculatedConfig) => void;
}> = ({ config, onChange }) => {
  const splitField = config.splitField || '';
  const splitDelimiter = config.splitDelimiter || '';
  const splitPartIndex = config.splitPartIndex ?? 0;
  const splitTrimResult = config.splitTrimResult !== false;

  const isCustomDelimiter = splitDelimiter !== '' && !SPLIT_DELIMITER_PRESETS.some(p => p.value === splitDelimiter);
  const [showCustom, setShowCustom] = useState(isCustomDelimiter);

  // Build example preview
  const exampleInput = 'T2443123696 / 0502569';
  const exampleParts = splitDelimiter ? exampleInput.split(splitDelimiter) : [exampleInput];

  return (
    <div className="space-y-2">
      <div className="flex items-center gap-1.5 mb-1">
        <Scissors className="w-3 h-3 text-sky-400" />
        <span className="text-[10px] font-semibold text-sky-400 uppercase">Split Text</span>
      </div>

      {/* Source Field */}
      <div className="bg-secondary/20 rounded-md p-2 border border-border/40 space-y-1.5">
        <div>
          <Label className="text-[9px] text-muted-foreground uppercase">Source Field</Label>
          <InlineFieldPicker
            value={splitField}
            onChange={(field) => onChange({ ...config, splitField: field })}
            label="Select field to split..."
          />
        </div>

        {/* Delimiter */}
        <div>
          <Label className="text-[9px] text-muted-foreground uppercase">Delimiter</Label>
          <Select
            value={showCustom ? '__custom' : (splitDelimiter || '__none')}
            onValueChange={(v) => {
              if (v === '__custom') {
                setShowCustom(true);
              } else if (v === '__none') {
                setShowCustom(false);
                onChange({ ...config, splitDelimiter: '' });
              } else {
                setShowCustom(false);
                onChange({ ...config, splitDelimiter: v });
              }
            }}
          >
            <SelectTrigger className="dark-input text-xs h-7"><SelectValue /></SelectTrigger>
            <SelectContent>
              {SPLIT_DELIMITER_PRESETS.map(preset => (
                <SelectItem key={preset.value} value={preset.value}>{preset.label}</SelectItem>
              ))}
              <SelectItem value="__custom">Custom...</SelectItem>
            </SelectContent>
          </Select>
          {showCustom && (
            <Input
              value={splitDelimiter}
              onChange={(e) => onChange({ ...config, splitDelimiter: e.target.value })}
              placeholder="Type your custom delimiter..."
              className="dark-input text-xs h-7 mt-1"
            />
          )}
        </div>

        {/* Part to keep */}
        <div>
          <Label className="text-[9px] text-muted-foreground uppercase">Part to Keep</Label>
          <Select
            value={String(splitPartIndex)}
            onValueChange={(v) => onChange({ ...config, splitPartIndex: Number(v) })}
          >
            <SelectTrigger className="dark-input text-xs h-7"><SelectValue /></SelectTrigger>
            <SelectContent>
              {[0, 1, 2, 3, 4, 5].map(idx => (
                <SelectItem key={idx} value={String(idx)}>
                  Part {idx + 1} {idx === 0 ? '(first)' : idx === 1 ? '(second)' : ''}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Trim result */}
        <div className="flex items-center gap-2 pt-1">
          <Checkbox
            id="split-trim"
            checked={splitTrimResult}
            onCheckedChange={(checked) => onChange({ ...config, splitTrimResult: !!checked })}
          />
          <label htmlFor="split-trim" className="text-[10px] text-muted-foreground cursor-pointer">
            Trim whitespace from result
          </label>
        </div>
      </div>

      {/* Live example preview */}
      <div className="bg-secondary/30 rounded px-2 py-1.5 border border-border/30">
        <p className="text-[9px] text-muted-foreground/60 uppercase mb-1">Example Preview</p>
        <div className="space-y-1">
          <div className="flex items-center gap-1.5">
            <span className="text-[9px] text-muted-foreground/50 w-10 flex-shrink-0">Input:</span>
            <span className="text-[10px] text-foreground/70 font-mono">{exampleInput}</span>
          </div>
          {splitDelimiter && (
            <div className="flex items-center gap-1.5">
              <span className="text-[9px] text-muted-foreground/50 w-10 flex-shrink-0">Delim:</span>
              <span className="text-[10px] text-orange-400 font-mono">"{splitDelimiter}"</span>
            </div>
          )}
          <div className="flex items-center gap-1.5">
            <span className="text-[9px] text-muted-foreground/50 w-10 flex-shrink-0">Parts:</span>
            <div className="flex items-center gap-0.5 flex-wrap">
              {exampleParts.map((part, idx) => (
                <React.Fragment key={idx}>
                  {idx > 0 && <span className="text-[9px] text-orange-400/50 mx-0.5">|</span>}
                  <span className={`text-[10px] font-mono px-1 py-0.5 rounded ${
                    idx === splitPartIndex
                      ? 'bg-sky-900/40 text-sky-400 border border-sky-700/50'
                      : 'text-muted-foreground/50'
                  }`}>
                    {splitTrimResult ? part.trim() : part}
                  </span>
                </React.Fragment>
              ))}
            </div>
          </div>
          <div className="flex items-center gap-1.5">
            <span className="text-[9px] text-muted-foreground/50 w-10 flex-shrink-0">Result:</span>
            <span className="text-[10px] text-sky-400 font-mono font-semibold">
              {splitPartIndex < exampleParts.length
                ? (splitTrimResult ? exampleParts[splitPartIndex].trim() : exampleParts[splitPartIndex])
                : '(no match)'}
            </span>
          </div>
        </div>
      </div>

      {/* Formula preview */}
      <div className="bg-secondary/30 rounded px-2 py-1.5 border border-border/30">
        <p className="text-[9px] text-muted-foreground/60 uppercase mb-0.5">Preview Formula</p>
        <p className="text-[10px] text-foreground/80 font-mono">
          <span className="text-sky-400">SPLIT</span>
          <span className="text-muted-foreground">(</span>
          <span className="text-blue-400">{SOURCE_FIELD_OPTIONS.find(f => f.field === splitField)?.label || splitField || '?'}</span>
          <span className="text-muted-foreground">, </span>
          <span className="text-orange-400">"{splitDelimiter || '?'}"</span>
          <span className="text-muted-foreground">)</span>
          <span className="text-sky-400">[{splitPartIndex + 1}]</span>
          {splitTrimResult && <span className="text-cyan-400/60 ml-1">.trim()</span>}
        </p>
      </div>
    </div>
  );
};

// ─── GST Builder ────────────────────────────────────────────────────


const GstBuilder: React.FC<{
  config: CalculatedConfig;
  onChange: (config: CalculatedConfig) => void;
  /** Extra fields from other template columns */
  extraFields?: SourceFieldOption[];
}> = ({ config, onChange, extraFields = [] }) => {
  const gstSourceField = config.gstSourceField || '';
  const gstRate = config.gstRate ?? 15;
  const gstInputMode: GstInputMode = config.gstInputMode || 'inclusive';
  const gstOutputMode: GstOutputMode = config.gstOutputMode || 'exclusive';
  const gstDecimalPlaces = config.gstDecimalPlaces ?? 2;

  // Resolve source field label (could be a calc col reference)
  const allFields = useMemo(() => [...SOURCE_FIELD_OPTIONS, ...extraFields], [extraFields]);
  const sourceFieldLabel = useMemo(() => {
    if (!gstSourceField) return '?';
    const found = allFields.find(f => f.field === gstSourceField);
    if (found) return found.label;
    if (isCalcColField(gstSourceField)) return `Col: ${getCalcColId(gstSourceField)}`;
    return gstSourceField;
  }, [gstSourceField, allFields]);

  // Example preview
  const exampleAmount = 115;
  let exInclusive: number, exExclusive: number, exGst: number;
  if (gstInputMode === 'inclusive') {
    exInclusive = exampleAmount;
    exExclusive = exampleAmount / (1 + gstRate / 100);
    exGst = exInclusive - exExclusive;
  } else {
    exExclusive = exampleAmount;
    exInclusive = exampleAmount * (1 + gstRate / 100);
    exGst = exampleAmount * (gstRate / 100);
  }
  const exResult = gstOutputMode === 'inclusive' ? exInclusive : gstOutputMode === 'exclusive' ? exExclusive : exGst;

  return (
    <div className="space-y-2">
      <div className="flex items-center gap-1.5 mb-1">
        <Receipt className="w-3 h-3 text-emerald-400" />
        <span className="text-[10px] font-semibold text-emerald-400 uppercase">GST Calculation</span>
      </div>

      <div className="bg-secondary/20 rounded-md p-2 border border-border/40 space-y-2">
        {/* Source Field */}
        <div>
          <Label className="text-[9px] text-muted-foreground uppercase">Source Amount Field</Label>
          <InlineFieldPicker
            value={gstSourceField}
            onChange={(field) => onChange({ ...config, gstSourceField: field })}
            label="Select amount field..."
            extraFields={extraFields}
          />
          {/* Show hint when a calc col is selected */}
          {gstSourceField && isCalcColField(gstSourceField) && (
            <div className="flex items-center gap-1 mt-1 text-[9px] text-amber-400/70">
              <Calculator className="w-2.5 h-2.5 flex-shrink-0" />
              <span>References another calculated column — GST will be computed on its output value</span>
            </div>
          )}
        </div>


        {/* GST Rate */}
        <div>
          <Label className="text-[9px] text-muted-foreground uppercase">GST Rate (%)</Label>
          <Input
            type="number"
            step="0.1"
            min={0}
            max={100}
            value={gstRate}
            onChange={(e) => onChange({ ...config, gstRate: Number(e.target.value) || 0 })}
            className="dark-input text-xs h-7"
          />
          <p className="text-[9px] text-muted-foreground/50 mt-0.5">NZ GST is 15%</p>
        </div>

        {/* Input Mode */}
        <div>
          <Label className="text-[9px] text-muted-foreground uppercase">Source Amount Is</Label>
          <div className="grid grid-cols-2 gap-1.5 mt-1">
            {(['inclusive', 'exclusive'] as GstInputMode[]).map(mode => {
              const info = GST_INPUT_MODE_INFO[mode];
              const isActive = gstInputMode === mode;
              return (
                <button
                  key={mode}
                  className={`px-2 py-1.5 rounded border text-[10px] text-left transition-colors ${
                    isActive
                      ? 'border-emerald-500/50 bg-emerald-900/30 text-emerald-300'
                      : 'border-border/60 text-muted-foreground hover:border-border'
                  }`}
                  onClick={() => onChange({ ...config, gstInputMode: mode })}
                >
                  <div className="font-medium">{info.label}</div>
                  <div className="text-[9px] opacity-60 mt-0.5">{info.description}</div>
                </button>
              );
            })}
          </div>
        </div>

        {/* Output Mode */}
        <div>
          <Label className="text-[9px] text-muted-foreground uppercase">Output</Label>
          <div className="space-y-1 mt-1">
            {(['inclusive', 'exclusive', 'gst_only'] as GstOutputMode[]).map(mode => {
              const info = GST_OUTPUT_MODE_INFO[mode];
              const isActive = gstOutputMode === mode;
              return (
                <button
                  key={mode}
                  className={`w-full px-2 py-1.5 rounded border text-[10px] text-left transition-colors flex items-center justify-between ${
                    isActive
                      ? 'border-emerald-500/50 bg-emerald-900/30 text-emerald-300'
                      : 'border-border/60 text-muted-foreground hover:border-border'
                  }`}
                  onClick={() => onChange({ ...config, gstOutputMode: mode })}
                >
                  <div>
                    <div className="font-medium">{info.label}</div>
                    <div className="text-[9px] opacity-60">{info.description}</div>
                  </div>
                  <span className="text-[9px] font-mono opacity-50">{info.formula}</span>
                </button>
              );
            })}
          </div>
        </div>

        {/* Decimal Places */}
        <div>
          <Label className="text-[9px] text-muted-foreground uppercase">Decimal Places</Label>
          <Select
            value={String(gstDecimalPlaces)}
            onValueChange={(v) => onChange({ ...config, gstDecimalPlaces: Number(v) })}
          >
            <SelectTrigger className="dark-input text-xs h-7"><SelectValue /></SelectTrigger>
            <SelectContent>
              {[0, 1, 2, 3, 4].map(d => (
                <SelectItem key={d} value={String(d)}>{d} decimal{d !== 1 ? 's' : ''}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Live Preview */}
      <div className="bg-emerald-900/15 border border-emerald-700/25 rounded px-2.5 py-2">
        <div className="flex items-center justify-between mb-1">
          <span className="text-[9px] text-emerald-400/70 uppercase">Example Preview</span>
          <span className="text-[9px] text-muted-foreground/50">
            ${exampleAmount.toFixed(2)} {gstInputMode === 'inclusive' ? '(incl. GST)' : '(excl. GST)'}
          </span>
        </div>
        <div className="grid grid-cols-3 gap-2 text-[10px]">
          <div className={`rounded px-1.5 py-1 ${gstOutputMode === 'inclusive' ? 'bg-emerald-900/30 border border-emerald-700/40' : 'bg-secondary/20'}`}>
            <div className="text-muted-foreground/60 text-[9px]">Incl. GST</div>
            <div className={`font-mono font-medium ${gstOutputMode === 'inclusive' ? 'text-emerald-400' : 'text-foreground/60'}`}>
              ${exInclusive.toFixed(gstDecimalPlaces)}
            </div>
          </div>
          <div className={`rounded px-1.5 py-1 ${gstOutputMode === 'exclusive' ? 'bg-emerald-900/30 border border-emerald-700/40' : 'bg-secondary/20'}`}>
            <div className="text-muted-foreground/60 text-[9px]">Excl. GST</div>
            <div className={`font-mono font-medium ${gstOutputMode === 'exclusive' ? 'text-emerald-400' : 'text-foreground/60'}`}>
              ${exExclusive.toFixed(gstDecimalPlaces)}
            </div>
          </div>
          <div className={`rounded px-1.5 py-1 ${gstOutputMode === 'gst_only' ? 'bg-emerald-900/30 border border-emerald-700/40' : 'bg-secondary/20'}`}>
            <div className="text-muted-foreground/60 text-[9px]">GST Only</div>
            <div className={`font-mono font-medium ${gstOutputMode === 'gst_only' ? 'text-emerald-400' : 'text-foreground/60'}`}>
              ${exGst.toFixed(gstDecimalPlaces)}
            </div>
          </div>
        </div>
        <div className="mt-1.5 text-[9px] text-emerald-400/60 font-mono">
          Result: <span className="text-emerald-400 font-semibold">${exResult.toFixed(gstDecimalPlaces)}</span>
        </div>
      </div>

      {/* Formula preview */}
      <div className="bg-secondary/30 rounded px-2 py-1.5 border border-border/30">
        <p className="text-[9px] text-muted-foreground/60 uppercase mb-0.5">Preview Formula</p>
        <p className="text-[10px] text-foreground/80 font-mono">
          <span className="text-emerald-400">GST_{gstOutputMode.toUpperCase()}</span>
          <span className="text-muted-foreground">(</span>
          <span className={isCalcColField(gstSourceField) ? 'text-amber-400' : 'text-blue-400'}>{sourceFieldLabel}</span>
          <span className="text-muted-foreground">, </span>
          <span className="text-green-400">{gstRate}%</span>
          <span className="text-muted-foreground">, </span>
          <span className="text-amber-400">{gstInputMode}</span>
          <span className="text-muted-foreground">)</span>
        </p>
      </div>
    </div>
  );
};



// ─── Excel Formula Builder ──────────────────────────────────────────

const ExcelFormulaBuilder: React.FC<{
  config: CalculatedConfig;
  onChange: (config: CalculatedConfig) => void;
  availableColumns: ExportTemplateColumn[];
}> = ({ config, onChange, availableColumns }) => {
  const formulaTemplate = config.excelFormulaTemplate || '';
  const resultType: ExcelFormulaResultType = config.excelFormulaResultType || 'number';
  const [insertColumnName, setInsertColumnName] = useState('__none');

  const insertToken = useCallback((token: string) => {
    const next = `${formulaTemplate}${formulaTemplate ? ' ' : ''}${token}`.trim();
    onChange({ ...config, excelFormulaTemplate: next });
  }, [config, formulaTemplate, onChange]);

  const insertOperator = useCallback((op: string) => {
    const next = `${formulaTemplate}${formulaTemplate ? ' ' : ''}${op}`.trim();
    onChange({ ...config, excelFormulaTemplate: next });
  }, [config, formulaTemplate, onChange]);

  const selectableColumns = useMemo(
    () => availableColumns
      .filter(col => col.is_enabled)
      .sort((a, b) => a.column_order - b.column_order),
    [availableColumns]
  );

  const handleInsertColumn = (value: string) => {
    setInsertColumnName(value);
    if (value === '__none') return;
    insertToken(`{{${value}}}`);
    setInsertColumnName('__none');
  };

  return (
    <div className="space-y-2">
      <div className="flex items-center gap-1.5 mb-1">
        <FileSpreadsheet className="w-3 h-3 text-cyan-400" />
        <span className="text-[10px] font-semibold text-cyan-400 uppercase">Excel Formula</span>
      </div>

      <div className="bg-secondary/20 rounded-md p-2 border border-border/40 space-y-2">
        <div>
          <Label className="text-[9px] text-muted-foreground uppercase">Insert Column Reference</Label>
          <Select value={insertColumnName} onValueChange={handleInsertColumn}>
            <SelectTrigger className="dark-input text-xs h-7">
              <SelectValue placeholder="Select a column to insert..." />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="__none">Select a column...</SelectItem>
              {selectableColumns.map(col => (
                <SelectItem key={col.id} value={col.export_column_name}>
                  {col.export_column_name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <p className="text-[9px] text-muted-foreground/50 mt-0.5">
            Column references are stored as placeholders like {'{{Sell Price}}'} and converted to real Excel cell references during XLSX export.
          </p>
        </div>

        <div>
          <Label className="text-[9px] text-muted-foreground uppercase">Formula Template</Label>
          <Textarea
            value={formulaTemplate}
            onChange={(e) => onChange({ ...config, excelFormulaTemplate: e.target.value })}
            placeholder="Example: {{Sell Price}} * 0.15"
            className="dark-input text-xs min-h-[88px] font-mono"
          />
          <p className="text-[9px] text-muted-foreground/50 mt-0.5">
            Use +, -, *, /, parentheses, and placeholders. You can type with or without the leading = sign.
          </p>
        </div>

        <div>
          <Label className="text-[9px] text-muted-foreground uppercase">Quick Insert</Label>
          <div className="flex flex-wrap gap-1 mt-1">
            {['+', '-', '*', '/', '(', ')', '0.15', '100'].map(op => (
              <Button
                key={op}
                type="button"
                size="sm"
                variant="outline"
                className="h-6 text-[10px] px-2 border-cyan-700/30 text-cyan-400 hover:bg-cyan-900/20"
                onClick={() => insertOperator(op)}
              >
                {op}
              </Button>
            ))}
          </div>
        </div>

        <div>
          <Label className="text-[9px] text-muted-foreground uppercase">Formula Result Type</Label>
          <div className="grid grid-cols-2 gap-1.5 mt-1">
            <button
              className={`px-2 py-1.5 rounded border text-[10px] text-left transition-colors ${
                resultType === 'number'
                  ? 'border-cyan-500/50 bg-cyan-900/30 text-cyan-300'
                  : 'border-border/60 text-muted-foreground hover:border-border'
              }`}
              onClick={() => onChange({ ...config, excelFormulaResultType: 'number' })}
            >
              <div className="font-medium">Number</div>
              <div className="text-[9px] opacity-60 mt-0.5">Best for prices, GST, totals</div>
            </button>
            <button
              className={`px-2 py-1.5 rounded border text-[10px] text-left transition-colors ${
                resultType === 'string'
                  ? 'border-cyan-500/50 bg-cyan-900/30 text-cyan-300'
                  : 'border-border/60 text-muted-foreground hover:border-border'
              }`}
              onClick={() => onChange({ ...config, excelFormulaResultType: 'string' })}
            >
              <div className="font-medium">String</div>
              <div className="text-[9px] opacity-60 mt-0.5">For formulas that return text</div>
            </button>
          </div>
        </div>
      </div>

      <div className="bg-cyan-900/15 border border-cyan-700/25 rounded px-2.5 py-2">
        <div className="flex items-center justify-between">
          <span className="text-[9px] text-cyan-400/70 uppercase">Preview</span>
          <span className="text-[9px] text-muted-foreground/50">XLSX only</span>
        </div>
        <div className="text-[10px] text-cyan-300 font-mono mt-1 break-all">
          {formulaTemplate ? (formulaTemplate.startsWith('=') ? formulaTemplate : `=${formulaTemplate}`) : '(no formula yet)'}
        </div>
      </div>

      <div className="flex items-start gap-1.5 text-[9px] text-cyan-400/70 bg-cyan-900/10 border border-cyan-700/20 rounded px-2 py-1">
        <FileSpreadsheet className="w-3 h-3 flex-shrink-0 mt-0.5" />
        <span>
          This calculation is written as a live Excel formula in the XLSX file. CSV exports cannot keep live spreadsheet formulas, so this mode is designed for XLSX.
        </span>
      </div>
    </div>
  );
};


// ─── Main Calculated Column Builder ─────────────────────────────────

const CalculatedColumnBuilder: React.FC<CalculatedColumnBuilderProps> = ({
  config,
  onChange,
  marginRules = [],
  otherTemplateRulesGroups = [],
  templateColumns = [],
  currentColumnId,
}) => {
  const currentCalcType = config?.calcType || null;

  // Combine current template margin rules with rules from other templates
  // This creates a single flat array that InlineFieldPicker and MathBuilder can use
  const allMarginRules: MarginRule[] = useMemo(() => {
    const otherRules = otherTemplateRulesGroups.flatMap(g => g.rules);
    return [...marginRules, ...otherRules];
  }, [marginRules, otherTemplateRulesGroups]);

  // Build extra fields from other template columns (excluding self)
  // These appear in field pickers so users can reference other columns' outputs
  const extraFields: SourceFieldOption[] = useMemo(() => {
    return templateColumns
      .filter(col => {
        // Exclude the current column to prevent circular references
        if (col.id === currentColumnId) return false;
        // Only include enabled columns that produce a value
        if (!col.is_enabled) return false;
        // Include calculated columns (math, gst, excel_formula, etc.) and source_field columns
        if (col.source_type === 'calculated' || col.source_type === 'source_field') return true;
        return false;
      })
      .map(col => {
        const calcType = col.source_type === 'calculated' ? col.transform_config?.calcType : null;
        const calcLabel = calcType ? CALC_TYPE_LABELS[calcType] || calcType : null;
        const description = col.source_type === 'calculated' && calcLabel
          ? calcLabel
          : col.source_type === 'source_field' && col.source_field
            ? col.source_field
            : col.source_type;

        return {
          field: makeCalcColField(col.id),
          label: col.export_column_name,
          group: 'Template Columns',
          description,
        };
      });
  }, [templateColumns, currentColumnId]);

  const handleCalcTypeChange = (calcType: CalcType) => {
    let newConfig: CalculatedConfig;

    switch (calcType) {
      case 'concat':
        newConfig = { calcType: 'concat', parts: [] };
        break;

      case 'math':
        newConfig = {
          calcType: 'math',
          inputs: [{ inputType: 'field', field: '' }],
        };
        break;

      case 'conditional_text':
        newConfig = {
          calcType: 'conditional_text',
          condition: { field: '', operator: 'is_not_blank' },
          thenValue: '',
          elseValue: '',
        };
        break;

      case 'split':
        newConfig = {
          calcType: 'split',
          splitField: '',
          splitDelimiter: ' / ',
          splitPartIndex: 0,
          splitTrimResult: true,
        };
        break;

      case 'gst':
        newConfig = {
          calcType: 'gst',
          gstSourceField: '',
          gstRate: 15,
          gstOutputMode: 'exclusive',
          gstInputMode: 'inclusive',
          gstDecimalPlaces: 2,
        };
        break;

      case 'excel_formula':
        newConfig = {
          calcType: 'excel_formula',
          excelFormulaTemplate: '',
          excelFormulaResultType: 'number',
        };
        break;

      default:
        newConfig = { calcType };
    }

    onChange(newConfig);
  };

  return (
    <div className="space-y-2 border-t border-border/30 pt-2 mt-2">
      <div className="flex items-center gap-1.5 mb-1">
        <Calculator className="w-3 h-3 text-amber-400" />
        <Label className="text-[10px] text-muted-foreground uppercase">Calculation Type</Label>
      </div>

      {/* Calc type selector */}
      <div className="grid grid-cols-2 gap-1">
        {(['concat', 'math', 'conditional_text', 'split', 'gst', 'excel_formula'] as CalcType[]).map(ct => {
          const info = CALC_TYPE_INFO[ct];
          const isActive = currentCalcType === ct;

          return (
            <button
              key={ct}
              type="button"
              className={`px-2 py-1.5 rounded text-[10px] border transition-colors text-center ${
                isActive
                  ? `${info.color} border-current`
                  : 'bg-secondary/30 text-muted-foreground border-border/40 hover:border-border'
              }`}
              onClick={() => handleCalcTypeChange(ct)}
            >
              {ct === 'concat' && <Link className="w-3 h-3 mx-auto mb-0.5" />}
              {ct === 'math' && <Calculator className="w-3 h-3 mx-auto mb-0.5" />}
              {ct === 'conditional_text' && <GitBranch className="w-3 h-3 mx-auto mb-0.5" />}
              {ct === 'split' && <Scissors className="w-3 h-3 mx-auto mb-0.5" />}
              {ct === 'gst' && <Receipt className="w-3 h-3 mx-auto mb-0.5" />}
              {ct === 'excel_formula' && <FileSpreadsheet className="w-3 h-3 mx-auto mb-0.5" />}
              <span className="block">{info.label}</span>
            </button>
          );
        })}
      </div>

      {/* Description */}
      {currentCalcType && (
        <p className="text-[9px] text-muted-foreground/60">
          {CALC_TYPE_INFO[currentCalcType].description}
        </p>
      )}

      {/* Builder for selected type */}
      {currentCalcType === 'concat' && config && (
        <ConcatBuilder config={config} onChange={onChange} />
      )}

      {currentCalcType === 'math' && config && (
        <MathBuilder
          config={config}
          onChange={onChange}
          marginRules={allMarginRules}
          extraFields={extraFields}
        />
      )}

      {currentCalcType === 'conditional_text' && config && (
        <ConditionalTextBuilder config={config} onChange={onChange} />
      )}

      {currentCalcType === 'split' && config && (
        <SplitBuilder config={config} onChange={onChange} />
      )}

      {currentCalcType === 'gst' && config && (
        <GstBuilder config={config} onChange={onChange} extraFields={extraFields} />
      )}

      {currentCalcType === 'excel_formula' && config && (
        <ExcelFormulaBuilder
          config={config}
          onChange={onChange}
          availableColumns={templateColumns.filter(col => col.id !== currentColumnId)}
        />
      )}

      {!currentCalcType && (
        <p className="text-[10px] text-muted-foreground/60 py-2 text-center">
          Select a calculation type above to configure this column.
        </p>
      )}
    </div>
  );
};

export default CalculatedColumnBuilder;
